/** Task categories: your own list (name + colour). Pill, picker, the Tasks page list, and a manager. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Archive, Check, Pencil, Plus, Tag, X } from "lucide-react";
import { useState } from "react";
import { api, patch, post, put } from "../lib/api";
import { ErrorNote, Modal } from "./ui";

export interface CategoryRef { id: string; name: string; color: string }
export interface Category extends CategoryRef { position: number; archived: boolean; open?: number }

export const useCategories = (archived = false) =>
  useQuery({ queryKey: ["categories", archived], queryFn: () => api<{ items: Category[]; palette: string[] }>(`/api/categories${archived ? "?archived=1" : ""}`), staleTime: 30_000 });

export function CategoryPill({ c, className, onClick }: { c: CategoryRef; className?: string; onClick?: () => void }) {
  const style = { color: c.color, backgroundColor: `color-mix(in srgb, ${c.color} 12%, transparent)`, borderColor: `color-mix(in srgb, ${c.color} 35%, transparent)` };
  const cls = clsx("inline-flex max-w-[12rem] items-center gap-1 truncate rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold", className);
  return onClick ? <button type="button" className={cls} style={style} onClick={onClick}>{c.name}</button> : <span className={cls} style={style}>{c.name}</span>;
}

/** Click the pill (or "Add category") to choose one of yours, none, or make a new one. */
export function CategoryPicker({ itemId, value, onChange }: { itemId: string; value: CategoryRef | null; onChange?: (c: CategoryRef | null) => void }) {
  const qc = useQueryClient();
  const cats = useCategories();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const set = useMutation({
    mutationFn: async (c: CategoryRef | null | { newName: string }) => {
      let id: string | null = null;
      if (c && "newName" in c) id = (await post<CategoryRef>("/api/categories", { name: c.newName })).id;
      else id = c?.id ?? null;
      return put<{ category: CategoryRef | null }>(`/api/work/items/${itemId}/category`, { categoryId: id });
    },
    onSuccess: (r) => {
      setOpen(false);
      setName("");
      onChange?.(r.category);
      void qc.invalidateQueries({ queryKey: ["categories"] });
      void qc.invalidateQueries({ queryKey: ["items"] });
      void qc.invalidateQueries({ queryKey: ["item", itemId] });
    },
  });
  return (
    <span className="relative inline-flex">
      {value ? <CategoryPill c={value} onClick={() => setOpen((x) => !x)} /> : <button type="button" className="muted inline-flex items-center gap-1 rounded-full border border-dashed border-[var(--line)] px-2 py-0.5 text-[0.7rem] hover:text-brand-700" onClick={() => setOpen((x) => !x)}><Tag size={11} /> Add category</button>}
      {open && (
        <span className="absolute left-0 top-7 z-30 w-60 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-2 shadow-[var(--shadow-pop)]">
          <span className="block max-h-60 space-y-0.5 overflow-auto">
            {(cats.data?.items ?? []).map((c) => (
              <button key={c.id} type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-black/[0.04]" onClick={() => set.mutate(c)}>
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: c.color }} />
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                {value?.id === c.id && <Check size={13} />}
              </button>
            ))}
            {value && <button type="button" className="muted flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-black/[0.04]" onClick={() => set.mutate(null)}><X size={13} /> No category</button>}
          </span>
          <form className="mt-1 flex gap-1 border-t border-[var(--line)] pt-2" onSubmit={(e) => { e.preventDefault(); if (name.trim()) set.mutate({ newName: name.trim() }); }}>
            <input className="input !py-1 text-sm" placeholder="New category…" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
            <button className="btn btn-sm" disabled={!name.trim() || set.isPending}><Plus size={13} /></button>
          </form>
          <ErrorNote error={set.error} />
        </span>
      )}
    </span>
  );
}

/** Left list on the Tasks page: All · each category with its open count · No category. */
export function CategoryList({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const cats = useCategories();
  const [manage, setManage] = useState(false);
  const row = (key: string, label: React.ReactNode, count?: number, color?: string) => (
    <li key={key}>
      <button className={clsx("flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm hover:bg-black/[0.04]", value === key && "bg-brand-50 font-semibold text-brand-800")} onClick={() => onChange(key)}>
        {color ? <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} /> : <span className="w-2.5" />}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {count !== undefined && <span className="muted text-xs">{count}</span>}
      </button>
    </li>
  );
  return (
    <div>
      <div className="mb-1 flex items-center justify-between px-2.5">
        <span className="section-label">Categories</span>
        <button className="muted text-xs hover:text-brand-700" onClick={() => setManage(true)}>Manage</button>
      </div>
      <ul className="space-y-0.5">
        {row("", "All")}
        {(cats.data?.items ?? []).map((c) => row(c.id, c.name, c.open, c.color))}
        {row("none", <span className="muted">No category</span>)}
      </ul>
      {!cats.data?.items.length && <p className="muted mt-2 px-2.5 text-xs">Type <b>/name</b> in the New line, e.g. “Order towels /procurement”.</p>}
      {manage && <ManageCategories onClose={() => setManage(false)} />}
    </div>
  );
}

function ManageCategories({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const cats = useCategories(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [newName, setNewName] = useState("");
  const refresh = () => void qc.invalidateQueries({ queryKey: ["categories"] });
  const upd = useMutation({ mutationFn: ({ id, ...b }: { id: string; name?: string; color?: string; archived?: boolean }) => patch(`/api/categories/${id}`, b), onSuccess: () => { setEditing(null); refresh(); void qc.invalidateQueries({ queryKey: ["items"] }); } });
  const add = useMutation({ mutationFn: () => post("/api/categories", { name: newName.trim() }), onSuccess: () => { setNewName(""); refresh(); } });
  const palette = cats.data?.palette ?? [];
  return (
    <Modal title="Your categories" onClose={onClose}>
      <p className="muted mb-3 text-xs">Only you see these. Archiving a category hides it; its tasks keep it until you change them.</p>
      <ErrorNote error={upd.error ?? add.error} />
      <ul className="divide-y divide-[var(--line)]">
        {(cats.data?.items ?? []).map((c) => (
          <li key={c.id} className={clsx("py-2", c.archived && "opacity-50")}>
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: c.color }} />
              {editing === c.id ? (
                <form className="flex flex-1 gap-1" onSubmit={(e) => { e.preventDefault(); upd.mutate({ id: c.id, name }); }}>
                  <input className="input !py-1 text-sm" autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
                  <button className="btn btn-sm btn-primary">Save</button>
                </form>
              ) : <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.name} {c.archived && <span className="muted text-xs">(archived)</span>}</span>}
              {editing !== c.id && <button className="btn btn-ghost btn-sm !px-1.5" title="Rename" onClick={() => { setEditing(c.id); setName(c.name); }}><Pencil size={13} /></button>}
              <button className="btn btn-ghost btn-sm !px-1.5" title={c.archived ? "Bring back" : "Archive"} onClick={() => upd.mutate({ id: c.id, archived: !c.archived })}><Archive size={13} /></button>
            </div>
            {!c.archived && (
              <div className="mt-1.5 flex gap-1 pl-5">
                {palette.map((col) => <button key={col} aria-label={`Colour ${col}`} className={clsx("h-4 w-4 rounded-full ring-offset-1", c.color === col && "ring-2 ring-[var(--text)]")} style={{ background: col }} onClick={() => upd.mutate({ id: c.id, color: col })} />)}
              </div>
            )}
          </li>
        ))}
      </ul>
      <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (newName.trim()) add.mutate(); }}>
        <input className="input text-sm" placeholder="Add a category…" value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={40} />
        <button className="btn" disabled={!newName.trim()}><Plus size={14} /> Add</button>
      </form>
    </Modal>
  );
}

/** A row of category chips to narrow a list: All · each category present · No category. */
export function CategoryFilter({ items, value, onChange }: { items: Array<{ category?: CategoryRef | null }>; value: string; onChange: (v: string) => void }) {
  const present = new Map<string, CategoryRef>();
  let none = 0;
  for (const i of items) i.category ? present.set(i.category.id, i.category) : none++;
  if (!present.size) return null;
  const chip = (key: string, label: React.ReactNode, color?: string) => (
    <button key={key} type="button" onClick={() => onChange(value === key ? "" : key)}
      className={clsx("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium", value === key ? "border-brand-400 bg-brand-50 text-brand-800" : "border-[var(--line)] hover:bg-black/[0.03]")}>
      {color && <span className="h-2 w-2 rounded-full" style={{ background: color }} />}{label}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-1.5">
      {chip("", "All")}
      {[...present.values()].sort((a, b) => a.name.localeCompare(b.name)).map((c) => chip(c.id, c.name, c.color))}
      {none > 0 && chip("none", "No category")}
    </div>
  );
}
export const matchCategory = (filter: string) => (i: { category?: CategoryRef | null }) => !filter || (filter === "none" ? !i.category : i.category?.id === filter);
